import React, { useState, useEffect } from 'react';
import SearchFieldsSelect, { useSearchFields, getSearchFieldsPlaceholder } from '../../Components/SearchFieldsSelect/SearchFieldsSelect';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { Table, Input, Space, Button, Typography, Tag, Select, DatePicker, Badge, Card, Tooltip, message, InputNumber, Avatar, Statistic, Row, Col, Pagination, Modal, Divider } from 'antd';
import { 
    CalendarOutlined, 
    EyeOutlined, 
    PhoneOutlined, 
    ReloadOutlined, 
    SearchOutlined, 
    UserSwitchOutlined,
    FilterOutlined,
    ClearOutlined,
    LeftOutlined,
    RightOutlined,
    DollarOutlined,
    EditOutlined,
    SaveOutlined,
    CloseOutlined,
    RiseOutlined,
    FallOutlined,
    ShopOutlined,
    CreditCardOutlined,
    FileTextOutlined,
    CopyOutlined,
    UserOutlined,
    MailOutlined,
    TeamOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
    SyncOutlined,
    CheckOutlined,CommentOutlined
} from '@ant-design/icons';
import API from '../../API/API';
import { TbCircleDottedLetterQ } from 'react-icons/tb';
import { motion } from 'framer-motion';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(relativeTime);

const { Text, Title } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;
const toPST = (date) => {
    if (!date) return null;
    return dayjs(date).tz('America/Los_Angeles');
};

// Helper function to get latest payment method
const getLatestPaymentMethod = (lead) => {
    if (!lead?.advancePayments || lead.advancePayments.length === 0) {
        return null;
    }
    const lastPayment = lead.advancePayments[lead.advancePayments.length - 1];
    return lastPayment?.paymentMethod;
};

// Calculate CC fee based on payment method
const calculateCCFee = (amount, paymentMethod) => {
    const method = paymentMethod;
    
    // 3.5% fee methods
    const threePointFivePercent = ['Card', 'Payment Link', 'Cashapp', 'Venmo', 'PayPal'];
    if (threePointFivePercent.includes(method)) {
        return parseFloat((amount * 0.035).toFixed(2));
    }
    
    // 6% fee methods
    if (method === 'Pay Tomorrow') {
        return parseFloat((amount * 0.06).toFixed(2));
    }
    
    // $35 flat fee
    if (method === 'Wire Transfer') {
        return 35.00;
    }
    
    // 0% fee methods (ACH, Zelle, Cash, Check Payment, Other)
    return 0;
};

const SaleMadeComplete = () => {
    const navigate = useNavigate();
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(false);
    const [dateRange, setDateRange] = useState([
    dayjs().startOf('month'),
    dayjs().endOf('month'),
]);
    const [loginAgent, setLoginAgent] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(30);
    const [totalRecords, setTotalRecords] = useState(0);
    const [searchTerm, setSearchTerm] = useState("");
    const [searchFields, setSearchFields] = useSearchFields('sale-made-complete:search-fields');
    // The "Search in" group only changes the results of a search, so changing it
    // with an empty search box sends no request.
    const effectiveSearchFields = searchTerm?.trim() ? searchFields : null;
    const [calledLeads, setCalledLeads] = useState(new Set());
    const [filterVisible, setFilterVisible] = useState(false);
    const [quickFilter, setQuickFilter] = useState('currentMonth');
    const [editingId, setEditingId] = useState(null);
    const [savingId, setSavingId] = useState(null);
    const [editingStageId, setEditingStageId] = useState(null);
    const [editingStageDate, setEditingStageDate] = useState(null);
    const [savingStageDate, setSavingStageDate] = useState(false);
    // Agent filter state
    const [agents, setAgents] = useState([]);
    const [selectedAgentId, setSelectedAgentId] = useState(null);
    const [serviceAgentsMap, setServiceAgentsMap] = useState({});
const [loadingServiceAgents, setLoadingServiceAgents] = useState(false);
const [selectedServiceAgentId, setSelectedServiceAgentId] = useState(null);
const [commentsModalVisible, setCommentsModalVisible] = useState(false);
    const [selectedLeadForComment, setSelectedLeadForComment] = useState(null);
    const [newComment, setNewComment] = useState('');
    const [addingComment, setAddingComment] = useState(false);
// Add these with your other state declarations
const [serviceAgentModalVisible, setServiceAgentModalVisible] = useState(false);
const [selectedLeadForServiceAgent, setSelectedLeadForServiceAgent] = useState(null);
const [selectedServiceAgentForLead, setSelectedServiceAgentForLead] = useState(null);
const [savingServiceAgent, setSavingServiceAgent] = useState(false);

    // Statistics
    const [statistics, setStatistics] = useState({
        totalLeads: 0,
        totalRevenue: 0,
        totalGP: 0,
        averageMargin: 0
    });

    const isAdmin = loginAgent?.role === "ADMIN";
const [editingSpecification, setEditingSpecification] = useState(null);
const [editingSpecValue, setEditingSpecValue] = useState('');
const [updatingSpecification, setUpdatingSpecification] = useState(false);
 

const handleUpdateSpecification = async (leadId, newSpecification) => {
    if (!leadId) return;
    
    const trimmedValue = newSpecification?.trim() || '';
    
    if (trimmedValue.length > 2000) {
        message.warning('Specification cannot exceed 2000 characters');
        return;
    }
    
    setUpdatingSpecification(true);
    try {
        // First, get the current lead data to preserve other vehicleInfo fields
        const currentLead = leads.find(lead => lead._id === leadId);
        const currentVehicleInfo = currentLead?.vehicleInfo || {};
        
        // Merge the specification with existing vehicleInfo
        const payload = {
            vehicleInfo: {
                ...currentVehicleInfo, // Preserve all existing fields
                specification: trimmedValue // Update only specification
            }
        };
        
        console.log('Sending payload with preserved data:', payload);
        
        const response = await API.UpdateLead(leadId, payload);
        
        if (response?.status) {
            message.success('Specification updated successfully!');
            fetchLeads(); // Refresh the leads list
        } else {
            message.error(response?.message || 'Failed to update specification');
        }
    } catch (error) {
        console.error('Error updating specification:', error);
        message.error('Failed to update specification');
    } finally {
        setUpdatingSpecification(false);
        setEditingSpecification(null);
        setEditingSpecValue('');
    }
};
    useEffect(() => {
        const UD = localStorage.getItem('userData');
        if (!UD) {
            navigate('/login');
        }
        const parsedUser = JSON.parse(UD);
        setLoginAgent(parsedUser);
        
        if (parsedUser?.role === "ADMIN") {
            fetchAgents();
        }
        fetchServiceAgents();
    }, []);
const handleDateRangeChange = (dates) => {
    if (dates && dates[0] && dates[1]) {
        setDateRange(dates);
    } else {
        // Reset to default (current month) if cancelled
        setDateRange([
            dayjs().startOf('month'),
            dayjs().endOf('month'),
        ]);
        setQuickFilter('currentMonth');
    }
    setCurrentPage(1);
};
// Handle opening the service agent selection modal with field type parameter
const handleServiceAgentEdit = (record, fieldType = 'serviceHandledBy') => {
    let currentServiceAgentId;
    switch(fieldType) {
        case 'orderServiceHandledBy':
            currentServiceAgentId = record.orderServiceHandledBy?._id || record.orderServiceHandledBy;
            break;
        case 'zohoServiceHandledBy':
            currentServiceAgentId = record.zohoServiceHandledBy?._id || record.zohoServiceHandledBy;
            break;
        case 'yardServiceHandledBy':
            currentServiceAgentId = record.yardServiceHandledBy?._id || record.yardServiceHandledBy;
            break;
        default:
            currentServiceAgentId = record.serviceHandledBy?._id || record.serviceHandledBy;
    }
    setSelectedLeadForServiceAgent(record);
    setSelectedServiceAgentForLead(currentServiceAgentId || null);
    setServiceAgentModalVisible(true);
};

// Handle saving the service agent with field type parameter
const handleServiceAgentSave = async (fieldType = 'serviceHandledBy') => {
    if (!selectedLeadForServiceAgent) return;
    
    setSavingServiceAgent(true);
    
    try {
        const payload = {
            [fieldType]: selectedServiceAgentForLead || null
        };
        
        const response = await API.UpdateLead(selectedLeadForServiceAgent._id, payload);
        
        if (response?.status || response?.success) {
            message.success('Service agent assigned successfully!');
            
            setLeads(prevLeads => 
                prevLeads.map(lead => 
                    lead._id === selectedLeadForServiceAgent._id ? { 
                        ...lead, 
                        [fieldType]: selectedServiceAgentForLead
                    } : lead
                )
            );
            
            setServiceAgentModalVisible(false);
            setSelectedLeadForServiceAgent(null);
            setSelectedServiceAgentForLead(null);
            fetchLeads();
            fetchServiceAgents();
        } else {
            message.error(response?.message || 'Failed to assign service agent');
        }
    } catch (error) {
        console.error('Error assigning service agent:', error);
        message.error('Failed to save service agent assignment');
    } finally {
        setSavingServiceAgent(false);
    }
};
    

        const handleAddComment = async () => {
        if (!newComment.trim()) {
            message.warning('Please enter a comment');
            return;
        }
    
        if (!selectedLeadForComment) return;
    
        setAddingComment(true);
        try {
            const existingComments = selectedLeadForComment.saleMadePageComment || [];
            const payload = {
                saleMadePageComment: [
                    ...existingComments,
                    {
                        comment: newComment.trim(),
                        changedAt: new Date().toISOString()
                    }
                ]
            };
    
            const response = await API.UpdateLead(selectedLeadForComment._id, payload);
    
            if (response?.status) {
                message.success('Comment added successfully!');
                setNewComment('');
                setCommentsModalVisible(false);
                setSelectedLeadForComment(null);
                // Refresh the leads list
                fetchLeads();
            } else {
                message.error(response?.message || 'Failed to add comment');
            }
        } catch (error) {
            console.error('Error adding comment:', error);
            message.error('Failed to add comment');
        } finally {
            setAddingComment(false);
        }
    };
    
    const formatCommentDate = (dateString) => {
        if (!dateString) return "";
        const date = new Date(dateString);
        return date.toLocaleDateString("en-US", {
            month: "short",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit"
        });
    };


// Get Service Agent Name (serviceHandledBy) - already exists
const getServiceAgentName = (record) => {
    const serviceAgentId = record.serviceHandledBy?._id || record.serviceHandledBy;
    if (!serviceAgentId) return null;
    
    if (record.serviceHandledBy && typeof record.serviceHandledBy === 'object') {
        return `${record.serviceHandledBy.firstName || ''} ${record.serviceHandledBy.lastName || ''}`.trim() || 
               record.serviceHandledBy.userName || 'Service Agent';
    }
    
    const agentInfo = serviceAgentsMap[serviceAgentId];
    return agentInfo?.name || null;
};

// Get Order Service Agent Name (orderServiceHandledBy)
const getOrderServiceAgentName = (record) => {
    const orderServiceAgentId = record.orderServiceHandledBy?._id || record.orderServiceHandledBy;
    if (!orderServiceAgentId) return null;
    
    if (record.orderServiceHandledBy && typeof record.orderServiceHandledBy === 'object') {
        return `${record.orderServiceHandledBy.firstName || ''} ${record.orderServiceHandledBy.lastName || ''}`.trim() || 
               record.orderServiceHandledBy.userName || 'Order Service Agent';
    }
    
    const agentInfo = serviceAgentsMap[orderServiceAgentId];
    return agentInfo?.name || null;
};

// Get Zoho Service Agent Name (zohoServiceHandledBy)
const getZohoServiceAgentName = (record) => {
    const zohoServiceAgentId = record.zohoServiceHandledBy?._id || record.zohoServiceHandledBy;
    if (!zohoServiceAgentId) return null;
    
    if (record.zohoServiceHandledBy && typeof record.zohoServiceHandledBy === 'object') {
        return `${record.zohoServiceHandledBy.firstName || ''} ${record.zohoServiceHandledBy.lastName || ''}`.trim() || 
               record.zohoServiceHandledBy.userName || 'Zoho Service Agent';
    }
    
    const agentInfo = serviceAgentsMap[zohoServiceAgentId];
    return agentInfo?.name || null;
};

// Get Yard Service Agent Name (yardServiceHandledBy)
const getYardServiceAgentName = (record) => {
    const yardServiceAgentId = record.yardServiceHandledBy?._id || record.yardServiceHandledBy;
    if (!yardServiceAgentId) return null;
    
    if (record.yardServiceHandledBy && typeof record.yardServiceHandledBy === 'object') {
        return `${record.yardServiceHandledBy.firstName || ''} ${record.yardServiceHandledBy.lastName || ''}`.trim() || 
               record.yardServiceHandledBy.userName || 'Yard Service Agent';
    }
    
    const agentInfo = serviceAgentsMap[yardServiceAgentId];
    return agentInfo?.name || null;
};

const fetchServiceAgents = async () => {
    setLoadingServiceAgents(true);
    try {
        const response = await API.userGetAll();
        if (response?.success) {
            const agentsMap = {};
            // Get all users with SERVICE-AGENT or SUPPORT-AGENT role
            const serviceAgentsList = (response.data || []).filter(
                user => user.role === "SERVICE-AGENT" || user.role === "SUPPORT-AGENT"
            );
            serviceAgentsList.forEach(agent => {
                agentsMap[agent._id] = {
                    name: `${agent.firstName || ''} ${agent.lastName || ''}`.trim() || agent.userName || agent.email,
                    email: agent.email,
                    phone: agent.directNumber,
                    role: agent.role
                };
            });
            setServiceAgentsMap(agentsMap);
        }
    } catch (error) {
        console.error("Failed to fetch service agents", error);
    } finally {
        setLoadingServiceAgents(false);
    }
};
    const fetchAgents = async () => {
        try {
            const response = await API.userGetAll();
            if (response?.success) {
                const salesAgents = (response.data || []).filter(user => user.role === "SALES-AGENT");
                setAgents(salesAgents);
            }
        } catch (error) {
            console.error("Failed to fetch agents", error);
        }
    };

    // Get agent name function
    const getAgentName = (record) => {
        if (record.assignedAgent?.name) return record.assignedAgent.name;
        if (record.agentName) return record.agentName;
        if (record.handledBy?.firstName && record.handledBy?.lastName) {
            return `${record.handledBy.firstName} ${record.handledBy.lastName}`;
        }
        if (record.handledBy?.name) return record.handledBy.name;
        return 'Unassigned';
    };

    // Quick filter options
    const handleQuickFilter = (value) => {
        setQuickFilter(value);
        let newDateRange;
        
        switch(value) {
            case 'today':
                newDateRange = [dayjs().startOf('day'), dayjs().endOf('day')];
                break;
            case 'yesterday':
                newDateRange = [dayjs().subtract(1, 'day').startOf('day'), dayjs().subtract(1, 'day').endOf('day')];
                break;
            case 'thisWeek':
                newDateRange = [dayjs().startOf('week'), dayjs().endOf('week')];
                break;
            case 'lastWeek':
                newDateRange = [dayjs().subtract(1, 'week').startOf('week'), dayjs().subtract(1, 'week').endOf('week')];
                break;
            case 'currentMonth':
                newDateRange = [dayjs().startOf('month'), dayjs().endOf('month')];
                break;
            case 'lastMonth':
                newDateRange = [dayjs().subtract(1, 'month').startOf('month'), dayjs().subtract(1, 'month').endOf('month')];
                break;
            case 'thisYear':
                newDateRange = [dayjs().startOf('year'), dayjs().endOf('year')];
                break;
            default:
                newDateRange = dateRange;
        }
        
        setDateRange(newDateRange);
        setCurrentPage(1);
    };
    const getSalesMadeDate = (lead) => {
    const salesStage = lead.leadStageHistory?.find(
        stage => stage.stage === 'Sales Made'
    );
    return salesStage?.changedAt || lead.orderDate || lead.createdAt;
};
const fetchLeads = async () => {
    setLoading(true);

    try {
        const userData = JSON.parse(localStorage.getItem("userData"));
        if (!userData?.id) {
            console.error("User authentication data not found");
            setLoading(false);
            return;
        }

        // Format dates for API filtering by orderDate
        let startDateStr = '';
        let endDateStr = '';
        
        const isSearching = searchTerm?.trim().length > 0;

        if (!isSearching) {
            if (Array.isArray(dateRange) && dateRange.length === 2 && 
        dateRange[0] && dateRange[1] && 
        dayjs.isDayjs(dateRange[0]) && dayjs.isDayjs(dateRange[1])) {
        startDateStr = dateRange[0].format('YYYY-MM-DD');
        endDateStr = dateRange[1].format('YYYY-MM-DD');
    } else {
        startDateStr = dayjs().startOf('month').format('YYYY-MM-DD');
        endDateStr = dayjs().endOf('month').format('YYYY-MM-DD');
    }
        }

        // Build params with orderDate filtering
        const params = {
            page: currentPage,
            limit: pageSize,
            status: "Sales Made",
            dateField: 'orderDate',  // 👈 Filter by orderDate
        };
        
        if (searchTerm?.trim()) {
            params.search = searchTerm.trim();
            params.searchFields = searchFields;
            // Don't send date filters when searching
        } else {
            params.startDate = startDateStr;
            params.endDate = endDateStr;
        }

        // Add agent filter for admin
        if (isAdmin && selectedAgentId) {
            params.agentId = selectedAgentId;
        }

        // The server sends only the fields this page uses
        // (LeadController LEAD_VIEW_FIELDS['sale-made-complete']).
        params.view = 'sale-made-complete';
        const response = await API.leadGetAll(params);

        if (response?.status) {
            let allLeads = response.data;
            
            // Sort by orderDate (Newest first)
            const sortedLeads = allLeads.sort((a, b) => {
                const dateA = new Date(a.orderDate || a.createdAt);
                const dateB = new Date(b.orderDate || b.createdAt);
                return dateB - dateA;
            });
            
            setLeads(sortedLeads);
setTotalRecords(response.pagination?.totalRecords || 0);
            
            // Calculate statistics
            const stats = sortedLeads.reduce((acc, lead) => {
                const data = computeSaleData(lead);
                return {
                    totalLeads: acc.totalLeads + 1,
                    totalRevenue: acc.totalRevenue + data.customerAmountReceived,
                    totalGP: acc.totalGP + data.totalGP,
                };
            }, { totalLeads: 0, totalRevenue: 0, totalGP: 0 });
            
            const averageMargin = stats.totalRevenue > 0 
                ? (stats.totalGP / stats.totalRevenue * 100).toFixed(1)
                : 0;
            
            setStatistics({
                ...stats,
                averageMargin
            });
        } else {
            console.error("Invalid response format:", response);
            setLeads([]);
        }

    } catch (error) {
        console.error('Failed to fetch leads:', error);
        setLeads([]);
    } finally {
        setLoading(false);
    }
};
useEffect(() => {
    const delaySearch = setTimeout(() => {
        fetchLeads();
    }, 500);

    return () => clearTimeout(delaySearch);
}, [currentPage, pageSize, dateRange, searchTerm, effectiveSearchFields, selectedAgentId]);
  const clearFilters = () => {
    setSearchTerm("");
    setDateRange([
        dayjs().startOf('month'),
        dayjs().endOf('month')
    ]);
    setQuickFilter('currentMonth');
    setSelectedAgentId(null);
    setCurrentPage(1);
    message.info('All filters cleared');
};
// For the table's onChange (if you need it)
const handleTableChange = (pagination) => {
    setCurrentPage(pagination.current);
    setPageSize(pagination.pageSize);
};

// For the pagination components directly
const handlePageChange = (page, pageSizeValue) => {
    setCurrentPage(page);
    setPageSize(pageSizeValue);
};

const handlePageSizeChange = (current, size) => {
    setCurrentPage(1); // Reset to first page when changing page size
    setPageSize(size);
};

    const computeSaleData = (lead) => {
        const totalAmount = Number(lead?.totalAmount || 0);
        const yardPrice = Number(lead?.saleMadeCharge?.yardPrice || 0);
        const paymentMethod = getLatestPaymentMethod(lead);
        
        const customerAmountReceived = totalAmount;
        const customerCCFee = calculateCCFee(customerAmountReceived, paymentMethod);
        const totalGP = parseFloat((customerAmountReceived - customerCCFee - yardPrice).toFixed(2));
        
        return {
            customerAmountReceived,
            customerCCFee,
            totalGP,
            yardPrice,
            paymentMethod
        };
    };

    const handleYardPriceChange = (leadId, value) => {
        setLeads(prevLeads => 
            prevLeads.map(lead => {
                if (lead._id === leadId) {
                    const updatedLead = { ...lead };
                    if (!updatedLead.saleMadeCharge) {
                        updatedLead.saleMadeCharge = {};
                    }
                    updatedLead.saleMadeCharge.yardPrice = value;
                    return updatedLead;
                }
                return lead;
            })
        );
    };

    const saveYardPrice = async (leadId) => {
        const lead = leads.find(l => l._id === leadId);
        if (!lead) return;

        setSavingId(leadId);
        try {
            const formData = {
                yardPrice: lead.saleMadeCharge?.yardPrice || 0
            };
            
            const res = await API.UpdateLead(leadId, { saleMadeCharge: formData });
            if (res?.status) {
                message.success("Yard price updated successfully");
                setEditingId(null);
                fetchLeads();
            } else {
                message.error("Update failed");
            }
        } catch (error) {
            console.error('Error updating yard price:', error);
            message.error("Update failed");
        } finally {
            setSavingId(null);
        }
    };

    const leadStageColors = {
        'New': { color: '#1890ff', background: '#e6f7ff' },
        'Call Back': { color: '#faad14', background: '#fffbe6' },
        'Voice Mail': { color: '#9254de', background: '#f9f0ff' },
        'Follow-Up': { color: '#fa8c16', background: '#fff7e6' },
        'Quoted': { color: '#13c2c2', background: '#e6fffb' },
        'Negotiation': { color: '#eb2f96', background: '#fff0f6' },
        'Pending Payment': { color: '#fa541c', background: '#fff2e8' },
        'Payment Completed': { color: '#52c41a', background: '#f6ffed' },
        'Sales Made': { color: '#389e0d', background: '#f6ffed' },
        'Part Not Available': { color: '#bfbfbf', background: '#fafafa' },
        'Wrong Number': { color: '#ff4d4f', background: '#fff1f0' },
        'Not Interested': { color: '#d46b08', background: '#fff7e6' },
        'Hang Up': { color: '#8c8c8c', background: '#f5f5f5' },
        'Fake': { color: '#a8071a', background: '#fff1f0' },
        'Test Lead': { color: '#7cb305', background: '#fcffe6' },
    };

    function formatToPST(isoDateString) {
        if (!isoDateString) return 'N/A';
        const date = new Date(isoDateString);
        const options = {
            timeZone: 'America/Los_Angeles',
            month: 'long',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: true
        };
        return date.toLocaleString('en-US', options);
    }

    // Agent column definition
    const agentColumn = isAdmin ? [{
        title: 'Sales-Agent',
        key: 'agent',
        width: 160,
        sorter: (a, b) => getAgentName(a).localeCompare(getAgentName(b)),
        render: (_, record) => {
            const agentName = getAgentName(record);
            const isUnassigned = agentName === 'Unassigned';
            return (
                <Tooltip title={agentName}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Avatar
                            size={32}
                            icon={<UserOutlined />}
                            style={{
                                backgroundColor: isUnassigned ? '#d9d9d9' : '#722ed1',
                                flexShrink: 0,
                            }}
                        >
                            {!isUnassigned && agentName?.[0]?.toUpperCase()}
                        </Avatar>
                        <Text
                            ellipsis={{ tooltip: true }}
                            style={{
                                fontSize: 13,
                                color: isUnassigned ? '#8c8c8c' : '#1f2937',
                                fontStyle: isUnassigned ? 'italic' : 'normal',
                            }}
                        >
                            {agentName}
                        </Text>
                    </div>
                </Tooltip>
            );
        },
    }] : [];

    const columns = [
      {
                   title: "Order Number",
                   width: 120, // Slightly increased width to accommodate date
                   key: "orderNumber",
                   render: (_, record) => {
                     const hasOrderNumber = record.orderNumber;
                     
                     return (
                       <div>
                         {/* Order Number Section */}
                         <div style={{ 
                           display: 'flex',
                           alignItems: 'center',
                           gap: 6,
                           background: hasOrderNumber ? '#f0f0ff' : '#fff1f0',
                           padding: '4px 8px',
                           borderRadius: 8,
                           width: 'fit-content'
                         }}>
                           {hasOrderNumber ? (
                             <FileTextOutlined style={{ color: '#722ed1', fontSize: 12 }} />
                           ) : (
                             <ClockCircleOutlined style={{ color: '#ff4d4f', fontSize: 12 }} />
                           )}
                           <Text style={{ 
                             color: hasOrderNumber ? '#722ed1' : '#ff4d4f',
                             fontSize: 12,
                             fontWeight: 600,
                             fontFamily: 'monospace',
                             fontStyle: hasOrderNumber ? 'normal' : 'italic'
                           }}>
                             {hasOrderNumber ? `#${record.orderNumber.slice(-8)}` : 'Waiting..'}
                           </Text>
                           {hasOrderNumber && (
                             <Tooltip title="Copy Order Number">
                               <Button
                                 type="text"
                                 size="small"
                                 icon={<CopyOutlined style={{ fontSize: 10 }} />}
                                 onClick={(e) => {
                                   e.stopPropagation();
                                   navigator.clipboard.writeText(record.orderNumber);
                                   message.success('Order number copied!');
                                 }}
                                 style={{
                                   padding: 0,
                                   height: 18,
                                   width: 18,
                                   minWidth: 18,
                                   color: '#722ed1',
                                 }}
                               />
                             </Tooltip>
                           )}
                         </div>
                         
                         {/* Date Section - Shows Order Date or Created Date */}
                         {(record.orderDate || record.createdAt) && (
                           <div style={{ 
                             marginTop: 6,
                             display: 'flex',
                             alignItems: 'center',
                             gap: 4,
                             paddingLeft: 4
                           }}>
                             <CalendarOutlined style={{ fontSize: 10, color: '#8c8c8c' }} />
                             <Tooltip 
                               title={dayjs(record.orderDate || record.createdAt).format('MMM DD, YYYY hh:mm A')}
                             >
                               <Text type="secondary" style={{ fontSize: 10 }}>
                                 {record.orderDate 
                                   ? dayjs(record.orderDate).format('MM/DD/YY hh:mm A')
                                   : ` ${dayjs(record.createdAt).format('MM/DD/YY hh:mm A')}`
                                 }
                               </Text>
                             </Tooltip>
                           </div>
                         )}
                       </div>
                     );
                   }
                 },
        ...agentColumn,

{
    title: 'Shipping Service-Agent',
    key: 'serviceAgent',
    width: 180,
   
    render: (_, record) => {
        const serviceAgentName = getServiceAgentName(record);
        const isAssigned = !!serviceAgentName;
        
        return (
            <div style={{ cursor: 'pointer' }} onClick={() => handleServiceAgentEdit(record, 'serviceHandledBy')}>
                <Tooltip title={isAssigned ? `Click to change service agent` : 'Click to assign service agent'}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Avatar size={32} icon={<TeamOutlined />} style={{ backgroundColor: isAssigned ? '#52c41a' : '#f0f0f0', flexShrink: 0 }}>
                            {isAssigned && serviceAgentName?.[0]?.toUpperCase()}
                        </Avatar>
                        <div style={{ flex: 1 }}>
                            {isAssigned ? (
                                <>
                                    <Text ellipsis={{ tooltip: true }} style={{ fontSize: 13, color: '#1f2937', display: 'block', fontWeight: 500 }}>
                                        {serviceAgentName}
                                    </Text>
                                    <Text type="secondary" style={{ fontSize: 10 }}>Click to edit</Text>
                                </>
                            ) : (
                                <Text style={{ fontSize: 13, color: '#1890ff', fontStyle: 'italic' }}>+ Assign Agent</Text>
                            )}
                        </div>
                        <EditOutlined style={{ fontSize: 12, color: '#1890ff', opacity: 0.6 }} />
                    </div>
                </Tooltip>
            </div>
        );
    },
},
// SERVICE AGENT 2: orderServiceHandledBy
{
    title: 'Order Service-Agent',
    key: 'orderServiceAgent',
    width: 180,
 
    render: (_, record) => {
        const agentName = getOrderServiceAgentName(record);
        const isAssigned = !!agentName;
        
        return (
            <div style={{ cursor: 'pointer' }} onClick={() => handleServiceAgentEdit(record, 'orderServiceHandledBy')}>
                <Tooltip title={isAssigned ? `Click to change order service agent` : 'Click to assign order service agent'}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Avatar size={32} icon={<TeamOutlined />} style={{ backgroundColor: isAssigned ? '#1890ff' : '#f0f0f0', flexShrink: 0 }}>
                            {isAssigned && agentName?.[0]?.toUpperCase()}
                        </Avatar>
                        <div style={{ flex: 1 }}>
                            {isAssigned ? (
                                <>
                                    <Text ellipsis={{ tooltip: true }} style={{ fontSize: 13, color: '#1f2937', display: 'block', fontWeight: 500 }}>
                                        {agentName}
                                    </Text>
                                    <Text type="secondary" style={{ fontSize: 10 }}>Click to edit</Text>
                                </>
                            ) : (
                                <Text style={{ fontSize: 13, color: '#1890ff', fontStyle: 'italic' }}>+ Assign Agent</Text>
                            )}
                        </div>
                        <EditOutlined style={{ fontSize: 12, color: '#1890ff', opacity: 0.6 }} />
                    </div>
                </Tooltip>
            </div>
        );
    },
},
// SERVICE AGENT 3: zohoServiceHandledBy
{
    title: 'Doc Service-Agent',
    key: 'zohoServiceAgent',
    width: 180,
   
    render: (_, record) => {
        const agentName = getZohoServiceAgentName(record);
        const isAssigned = !!agentName;
        
        return (
            <div style={{ cursor: 'pointer' }} onClick={() => handleServiceAgentEdit(record, 'zohoServiceHandledBy')}>
                <Tooltip title={isAssigned ? `Click to change Zoho service agent` : 'Click to assign Zoho service agent'}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Avatar size={32} icon={<TeamOutlined />} style={{ backgroundColor: isAssigned ? '#faad14' : '#f0f0f0', flexShrink: 0 }}>
                            {isAssigned && agentName?.[0]?.toUpperCase()}
                        </Avatar>
                        <div style={{ flex: 1 }}>
                            {isAssigned ? (
                                <>
                                    <Text ellipsis={{ tooltip: true }} style={{ fontSize: 13, color: '#1f2937', display: 'block', fontWeight: 500 }}>
                                        {agentName}
                                    </Text>
                                    <Text type="secondary" style={{ fontSize: 10 }}>Click to edit</Text>
                                </>
                            ) : (
                                <Text style={{ fontSize: 13, color: '#1890ff', fontStyle: 'italic' }}>+ Assign Agent</Text>
                            )}
                        </div>
                        <EditOutlined style={{ fontSize: 12, color: '#1890ff', opacity: 0.6 }} />
                    </div>
                </Tooltip>
            </div>
        );
    },
},
// SERVICE AGENT 4: yardServiceHandledBy
{
    title: 'Yard Service-Agent',
    key: 'yardServiceAgent',
    width: 180,
   
    render: (_, record) => {
        const agentName = getYardServiceAgentName(record);
        const isAssigned = !!agentName;
        
        return (
            <div style={{ cursor: 'pointer' }} onClick={() => handleServiceAgentEdit(record, 'yardServiceHandledBy')}>
                <Tooltip title={isAssigned ? `Click to change yard service agent` : 'Click to assign yard service agent'}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Avatar size={32} icon={<TeamOutlined />} style={{ backgroundColor: isAssigned ? '#eb2f96' : '#f0f0f0', flexShrink: 0 }}>
                            {isAssigned && agentName?.[0]?.toUpperCase()}
                        </Avatar>
                        <div style={{ flex: 1 }}>
                            {isAssigned ? (
                                <>
                                    <Text ellipsis={{ tooltip: true }} style={{ fontSize: 13, color: '#1f2937', display: 'block', fontWeight: 500 }}>
                                        {agentName}
                                    </Text>
                                    <Text type="secondary" style={{ fontSize: 10 }}>Click to edit</Text>
                                </>
                            ) : (
                                <Text style={{ fontSize: 13, color: '#1890ff', fontStyle: 'italic' }}>+ Assign Agent</Text>
                            )}
                        </div>
                        <EditOutlined style={{ fontSize: 12, color: '#1890ff', opacity: 0.6 }} />
                    </div>
                </Tooltip>
            </div>
        );
    },
},
      {
    title: "Vehicle Info",
    key: "vehicleInfo",
    width: 200,
    sorter: (a, b) => {
        const yearA = a.vehicleInfo?.year || 0;
        const yearB = b.vehicleInfo?.year || 0;
        if (yearA !== yearB) return yearA - yearB;
        
        const makeA = a.vehicleInfo?.make || '';
        const makeB = b.vehicleInfo?.make || '';
        return makeA.localeCompare(makeB);
    },
    render: (_, record) => (
        <div style={{ padding: '8px 0' }}>
            {/* Year and Part badges */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <Tag color="blue" style={{ borderRadius: 12, fontSize: 11, margin: 0 }}>
                    {record.vehicleInfo?.year || 'N/A'}
                </Tag>
                <Tag color="cyan" style={{ borderRadius: 12, fontSize: 11, margin: 0 }}>
                    {record.vehicleInfo?.part || 'N/A'}
                </Tag>
            </div>

            {/* Vehicle Make & Model */}
            <div style={{ marginBottom: 4 }}>
                <Text strong style={{ fontSize: 13 }}>
                    {record.vehicleInfo?.make || 'N/A'} {record.vehicleInfo?.model || ''}
                </Text>
                {record.queueLead === true && (
                    <Tooltip title="Queued Lead">
                        <TbCircleDottedLetterQ style={{ marginLeft: 6, fontSize: "14px", color: "#1677ff" }} />
                    </Tooltip>
                )}
            </div>

            {/* VIN */}
            {record.vehicleInfo?.vin && (
                <Text type="secondary" style={{ fontSize: 10, display: 'block' }}>
                    VIN: {record.vehicleInfo.vin}
                </Text>
            )}

            {/* Variant */}
            {record.vehicleInfo?.variant && (
                <Text type="secondary" style={{ fontSize: 11, marginTop: 4, display: 'block' }}>
                    Variant: {record.vehicleInfo.variant}
                </Text>
            )}
        </div>
    )
},
    {
    title: 'Specification',
    key: 'specification',
    width: 250,
    render: (_, record) => {
        const specification = record.vehicleInfo?.specification || '';
        const isEditing = editingSpecification === record._id;
        
        if (isEditing) {
            return (
                <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                    <Input.TextArea
                        autoSize={{ minRows: 2, maxRows: 4 }}
                        value={editingSpecValue}
                        onChange={(e) => setEditingSpecValue(e.target.value)}
                        placeholder="Enter specification details..."
                        style={{ flex: 1, fontSize: '12px' }}
                        autoFocus
                        maxLength={2000}
                        showCount
                    />
                    <div style={{ display: 'flex', gap: '4px', flexDirection: 'column' }}>
                        <Button
                            size="small"
                            type="primary"
                            icon={<CheckOutlined />}
                            loading={updatingSpecification}
                            onClick={() => handleUpdateSpecification(record._id, editingSpecValue)}
                            style={{ minWidth: '32px' }}
                        />
                        <Button
                            size="small"
                            icon={<CloseOutlined />}
                            onClick={() => {
                                setEditingSpecification(null);
                                setEditingSpecValue('');
                            }}
                        />
                    </div>
                </div>
            );
        }
        
        return (
            <div 
                style={{ 
                    cursor: 'pointer',
                    padding: '8px',
                    borderRadius: '6px',
                    transition: 'all 0.3s',
                    backgroundColor: specification ? 'transparent' : '#fafafa',
                    border: '1px solid transparent',
                    minHeight: '60px'
                }}
                onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#f5f5f5';
                    e.currentTarget.style.borderColor = '#d9d9d9';
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = specification ? 'transparent' : '#fafafa';
                    e.currentTarget.style.borderColor = 'transparent';
                }}
                onClick={(e) => {
                    e.stopPropagation();
                    setEditingSpecification(record._id);
                    setEditingSpecValue(specification);
                }}
            >
                {specification ? (
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                            <FileTextOutlined style={{ color: '#52c41a', fontSize: '12px' }} />
                            <Text strong style={{ fontSize: '12px', color: '#52c41a' }}>Specification:</Text>
                        </div>
                        <Text style={{ 
                            fontSize: '12px', 
                            whiteSpace: 'pre-wrap', 
                            wordBreak: 'break-word',
                            display: 'block',
                            lineHeight: '1.5'
                        }}>
                            {specification.length > 100 
                                ? `${specification.substring(0, 100)}...` 
                                : specification}
                        </Text>
                        <div style={{ marginTop: '4px' }}>
                            <Text type="secondary" style={{ fontSize: '10px' }}>
                                <EditOutlined style={{ marginRight: '4px' }} />
                                Click to edit
                            </Text>
                        </div>
                    </div>
                ) : (
                    <div style={{ textAlign: 'center', padding: '12px' }}>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                            <FileTextOutlined style={{ marginRight: '4px', color: '#52c41a' }} />
                            <EditOutlined style={{ marginRight: '4px', fontSize: '10px' }} />
                            Click to add specification
                        </Text>
                    </div>
                )}
            </div>
        );
    },
}, 
        {
            title: "Customer",
            width: 220,
            dataIndex: ["personalInfo", "fullName"],
            key: "fullName",
            render: (text, record) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <Avatar 
                        size={36}
                        icon={<UserOutlined />} 
                        style={{ 
                            background: `linear-gradient(135deg, #1890ff 0%, #40a9ff 100%)`,
                            flexShrink: 0
                        }}
                    />
                    <div>
                        <Space align="center" size={4}>
                            <Text strong style={{ fontSize: 13 }}>
                                {record.personalInfo.fullName || 'N/A'}
                            </Text>
                            {record.isExistingCustomer === true && (
                                <Tooltip title="Existing Customer">
                                    <UserSwitchOutlined style={{ fontSize: "12px", color: "#52c41a" }} />
                                </Tooltip>
                            )}
                        </Space>
                        <br />
                        <Text type="secondary" style={{ fontSize: 11 }}>
                            {record.personalInfo.phoneNumber || 'N/A'}
                        </Text>
                        <br />
                          <Text type="secondary" style={{ fontSize: 11 }}>
                            {record.personalInfo.email || 'N/A'}
                        </Text>
                    </div>
                </div>
            )
        },
// Add these columns after the CUSTOMER column and before the Comment column

{
    title: "DAYS PROMISED",
    key: "trackingPromiseDays",
    width: 120,
    render: (_, record) => {
        if (!record.trackingPromiseDays) {
            return <Text type="secondary" style={{ fontSize: 12 }}>—</Text>;
        }
        return (
            <Tooltip title="Days promised for tracking">
                <Tag color="cyan" style={{ borderRadius: 12, padding: '4px 8px' }}>
                    <CalendarOutlined style={{ marginRight: 4 }} />
                    {record.trackingPromiseDays} days
                </Tag>
            </Tooltip>
        );
    },
},
{
    title: "FINANCIAL",
    key: "financial",
    width: 200,
    render: (_, record) => {
        // Only show for Sales Made status
        if (record.leadStage !== "Sales Made") {
            return (
                <Card size="small" style={{ borderRadius: 10, background: '#fafafa', border: 'none' }} bodyStyle={{ padding: '8px 12px' }}>
                    <Text type="secondary" style={{ fontSize: 12, textAlign: 'center', display: 'block' }}>
                        Not available
                    </Text>
                </Card>
            );
        }

        // Get sale made charge data
        const sale = record.saleMadeCharge || {};
        const totalGP = sale.totalGP || 0;
        
        // Get payment method from advancePayments
        const getPaymentMethod = () => {
            if (!record?.advancePayments || record.advancePayments.length === 0) {
                return null;
            }
            const lastPayment = record.advancePayments[record.advancePayments.length - 1];
            return lastPayment?.paymentMethod;
        };
        
        const paymentMethod = getPaymentMethod();
        
        // Calculate fee badge text
        const getFeeBadge = () => {
            const threePointFivePercent = ['Card', 'Payment Link', 'Cashapp', 'Venmo', 'PayPal'];
            if (threePointFivePercent.includes(paymentMethod)) {
                return "3.5%";
            }
            if (paymentMethod === 'Pay Tomorrow') {
                return "6%";
            }
            if (paymentMethod === 'Wire Transfer') {
                return "$35";
            }
            return "0%";
        };

        // Get the latest yard final price - this ensures it matches SaleMadeTab
        const getLatestYardFinalPrice = () => {
            // Get the current yard selection
            const yards = record.yard || [];
            const currentYard = yards.find(yard => yard.isCurrent === true) || yards[yards.length - 1];
            return currentYard?.finalPrice || sale.yardPrice || 0;
        };

        const yardFinalPrice = getLatestYardFinalPrice();
        
        return (
            <Card 
                size="small" 
                style={{ 
                    borderRadius: 10, 
                    background: '#fafafa', 
                    border: 'none',
                    position: 'relative'
                }} 
                bodyStyle={{ padding: '8px 12px' }}
            >
                {/* Fee Badge */}
                {paymentMethod && (
                    <Tag 
                        color={paymentMethod === 'Wire Transfer' ? 'orange' : 'purple'}
                        style={{ 
                            position: 'absolute', 
                            top: 8, 
                            right: 8,
                            fontSize: 10,
                            padding: '0 6px',
                            margin: 0
                        }}
                    >
                        {getFeeBadge()}
                    </Tag>
                )}
                
                {/* Amount Received */}
                <div style={{ marginBottom: 8 }}>
                    <Space size={4} direction="vertical" style={{ width: '100%' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <DollarOutlined style={{ color: '#52c41a', fontSize: 12 }} />
                            <Text type="secondary" style={{ fontSize: 11 }}>Amount Received:</Text>
                        </div>
                        <Text strong style={{ fontSize: 14, color: '#7c3aed' }}>
                            ${sale.customerAmountReceived?.toFixed(2) || '0.00'}
                        </Text>
                    </Space>
                </div>
                
                {/* Divider */}
                <div style={{ height: 1, background: '#e8e8e8', margin: '8px 0' }} />
                
                {/* Yard Price - NOW USING finalPrice from yard */}
                <div style={{ marginBottom: 8 }}>
                    <Space size={4} direction="vertical" style={{ width: '100%' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <ShopOutlined style={{ color: '#722ed1', fontSize: 12 }} />
                            <Text type="secondary" style={{ fontSize: 11 }}>Yard Price:</Text>
                        </div>
                        <Text strong style={{ fontSize: 13, color: '#dc2626' }}>
                            ${yardFinalPrice.toFixed(2)}
                        </Text>
                    </Space>
                </div>
                
                {/* Divider */}
                <div style={{ height: 1, background: '#e8e8e8', margin: '8px 0' }} />
                
                {/* CC Fee */}
                <div style={{ marginBottom: 8 }}>
                    <Space size={4} direction="vertical" style={{ width: '100%' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <CreditCardOutlined style={{ color: '#d97706', fontSize: 12 }} />
                            <Text type="secondary" style={{ fontSize: 11 }}>CC Fee:</Text>
                        </div>
                        <Text strong style={{ fontSize: 13, color: '#d97706' }}>
                            ${sale.customerCCFee?.toFixed(2) || '0.00'}
                        </Text>
                    </Space>
                </div>
                
                {/* Divider */}
                <div style={{ height: 1, background: '#e8e8e8', margin: '8px 0' }} />
                
                {/* Total GP */}
                <div>
                    <Space size={4} direction="vertical" style={{ width: '100%' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <RiseOutlined style={{ color: totalGP >= 0 ? '#059669' : '#dc2626', fontSize: 12 }} />
                            <Text type="secondary" style={{ fontSize: 11 }}>Total GP:</Text>
                        </div>
                        <Tag 
                            color={totalGP >= 0 ? "success" : "error"}
                            style={{ 
                                borderRadius: 12, 
                                padding: '2px 10px',
                                fontSize: 12,
                                fontWeight: 600,
                                marginTop: 2
                            }}
                        >
                            ${totalGP.toFixed(2)}
                        </Tag>
                    </Space>
                </div>
                
                {/* Margin indicator */}
                {sale.customerAmountReceived > 0 && (
                    <div style={{ 
                        marginTop: 8,
                        paddingTop: 6,
                        borderTop: '1px dashed #e8e8e8',
                        textAlign: 'center'
                    }}>
                        <Text type="secondary" style={{ fontSize: 10 }}>
                            Margin: {((sale.totalGP / sale.customerAmountReceived) * 100).toFixed(1)}%
                        </Text>
                    </div>
                )}
            </Card>
        );
    },
},
{
    title: "LEAD TYPE",
    key: "typeOfLead",
    width: 140,
    render: (_, record) => {
        const getLeadTypeColor = (type) => {
            switch(type) {
                case 'Inbound Call':
                    return { color: '#1890ff', background: '#e6f7ff', icon: <PhoneOutlined /> };
                case 'Live':
                    return { color: '#52c41a', background: '#f6ffed', icon: <CheckCircleOutlined /> };
                case 'Follow-Up':
                    return { color: '#faad14', background: '#fffbe6', icon: <CalendarOutlined /> };
                default:
                    return { color: '#8c8c8c', background: '#f5f5f5', icon: <FileTextOutlined /> };
            }
        };
        
        const typeConfig = getLeadTypeColor(record.typeOfLead || 'N/A');
        
        return (
            <Tag
                style={{
                    color: typeConfig.color,
                    backgroundColor: typeConfig.background,
                    borderRadius: 16,
                    fontWeight: 500,
                    padding: '6px 12px',
                    border: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    width: 'fit-content'
                }}
            >
                {typeConfig.icon}
                <span>{record.typeOfLead || 'N/A'}</span>
            </Tag>
        );
    },
},
      
         {
          title: 'Comment',
          key: 'saleMadePageComment',
          width: 280,
          render: (_, record) => {
              const comments = record.saleMadePageComment || [];
              const latestComment = comments.length > 0 ? comments[comments.length - 1] : null;
              
              return (
                  <div>
                   
      
                      {/* Display Latest Comment */}
                      {latestComment ? (
                          <div 
                              style={{
                                  background: '#fafafa',
                                  padding: '8px 10px',
                                  borderRadius: '8px',
                                  border: '1px solid #f0f0f0',
                                  cursor: 'pointer',
                                  transition: 'all 0.3s ease',
                                  wordBreak: 'break-word',      // Add this
                                  wordWrap: 'break-word',       // Add this
                                  overflowWrap: 'break-word',   // Add this
                                  whiteSpace: 'normal'          // Add this
                              }}
                              onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedLeadForComment(record);
                                  setCommentsModalVisible(true);
                              }}
                          >
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
                                  <Text type="secondary" style={{ fontSize: '10px' }}>
                                      <ClockCircleOutlined style={{ marginRight: '4px' }} />
                                      {formatCommentDate(latestComment.changedAt)}
                                  </Text>
                                  {comments.length > 1 && (
                                      <Tag 
                                          color="blue" 
                                          style={{ 
                                              fontSize: '10px', 
                                              padding: '0 4px', 
                                              margin: 0,
                                              cursor: 'pointer'
                                          }}
                                          onClick={(e) => {
                                              e.stopPropagation();
                                              setSelectedLeadForComment(record);
                                              setCommentsModalVisible(true);
                                          }}
                                      >
                                          +{comments.length - 1} more
                                      </Tag>
                                  )}
                              </div>
                              <Text style={{ 
                                  fontSize: '12px', 
                                  display: 'block', 
                                  wordBreak: 'break-word',    // Add this
                                  whiteSpace: 'pre-wrap'       // Add this for preserving whitespace and wrapping
                              }}>
                                  {latestComment.comment.length > 80 
                                      ? `${latestComment.comment.substring(0, 80)}...` 
                                      : latestComment.comment}
                              </Text>
                          </div>
                      ) : (
                          <div 
                              style={{ 
                                  textAlign: 'center', 
                                  padding: '12px',
                                  background: '#fafafa',
                                  borderRadius: '8px',
                                  border: '1px solid #f0f0f0',
                                  cursor: 'pointer'
                              }}
                              onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedLeadForComment(record);
                                  setCommentsModalVisible(true);
                              }}
                          >
                              <Text type="secondary" style={{ fontSize: '12px' }}>
                                  <CommentOutlined style={{ marginRight: '4px', fontSize: '14px' }} />
                                  No comments yet
                              </Text>
                          </div>
                      )}
                         <Button
                          type="primary"
                          size="small"
                          icon={<CommentOutlined />}
                          onClick={(e) => {
                              e.stopPropagation();
                              setSelectedLeadForComment(record);
                              setNewComment('');
                              setCommentsModalVisible(true);
                          }}
                          style={{ 
                              marginBottom: '12px',
                              width: '100%',
                              fontSize: '12px'
                          }}
                      >
                          Add Comment
                      </Button>
                  </div>
              );
          },
      },
        {
    title: 'Sale Made Date',
    key: 'leadStage',
    width: 220,
    render: (_, record) => {
        const stage = record.leadStage;
        const isEditing = editingStageId === record._id;
        const styleConfig = leadStageColors[stage] || {
            color: '#000000',
            background: '#f5f5f5',
        };
        
        // Get latest stage change date for the current stage
        const getLatestStageDatePST = () => {
            if (record.leadStageHistory && record.leadStageHistory.length > 0) {
                const currentStageEntry = record.leadStageHistory.find(
                    history => history.stage === stage
                );
                
                if (currentStageEntry) {
                    return toPST(currentStageEntry.changedAt);
                }
                
                const latest = [...record.leadStageHistory].sort((a, b) => 
                    new Date(b.changedAt) - new Date(a.changedAt)
                )[0];
                return toPST(latest.changedAt);
            }
            return null;
        };
        
        const stageDatePST = getLatestStageDatePST();
        
        // Handle date update - Modified to also update orderDate for Sales Made stage
        const handleDateUpdate = async () => {
            if (!editingStageDate) {
                message.warning('Please select a date');
                return;
            }
            
            setSavingStageDate(true);
            
            try {
                const currentHistory = record.leadStageHistory || [];
                const stageIndex = currentHistory.findIndex(
                    history => history.stage === stage
                );
                
                let updatedHistory;
                // Convert the selected date to UTC for storage
                const newDateUTC = editingStageDate
                    .tz('America/Los_Angeles')
                    .hour(0)
                    .minute(0)
                    .second(0)
                    .millisecond(0)
                    .toISOString();
                
                if (stageIndex !== -1) {
                    updatedHistory = [...currentHistory];
                    updatedHistory[stageIndex] = {
                        ...updatedHistory[stageIndex],
                        changedAt: newDateUTC
                    };
                } else {
                    updatedHistory = [
                        ...currentHistory,
                        {
                            stage: stage,
                            changedAt: newDateUTC,
                            comment: `Date updated for ${stage}`
                        }
                    ];
                }
                
                // Prepare update object
                const updateData = {
                    leadStageHistory: updatedHistory
                };
                
                // If the stage being edited is "Sales Made", also update orderDate
                if (stage === 'Sales Made') {
                    updateData.orderDate = newDateUTC;
                    console.log('Updating orderDate as well:', newDateUTC);
                }
                
                const response = await API.UpdateLead(record._id, updateData);
                
                if (response.status) {
                    message.success(stage === 'Sales Made' ? 'Sales date and order date updated successfully' : 'Stage date updated successfully');
                    setEditingStageId(null);
                    setEditingStageDate(null);
                    fetchLeads(); // Refresh the data
                } else {
                    message.error(response?.message || 'Failed to update date');
                }
            } catch (error) {
                console.error('Error updating stage date:', error);
                message.error('Failed to update date');
            } finally {
                setSavingStageDate(false);
            }
        };
        
        return (
            <div>
                                   <Tag
                        style={{
                            color: styleConfig.color,
                            backgroundColor: styleConfig.background,
                            borderRadius: 16,
                            fontWeight: 500,
                            padding: '4px 12px',
                            border: 'none',
                            textAlign: 'center'
                        }}
                    >
                        {stage || 'N/A'}
                    </Tag>
                    
                
                {stageDatePST && !isEditing && (
                    <div style={{ marginTop: 4 }}>
                        <Tooltip title={stageDatePST.format('MM-DD-YYYY hh:mm A') + ' PST'}>
                            <Space size={4}>
                                <ClockCircleOutlined style={{ fontSize: 12, fontWeight: 500, lineHeight: 1.2 }} />
                            

                                <Text style={{ fontSize: 12, fontWeight: 500, lineHeight: 1.2 }}>
 {stageDatePST.format('MM/DD/YY · hh:mm A')} PST
                         </Text>
                            </Space>
                        </Tooltip>

                           {!isEditing && stageDatePST && (
                        <Tooltip title="Edit Date">
                            <Button
                                type="text"
                                size="small"
                                icon={<EditOutlined />}
                                onClick={() => {
                                    setEditingStageId(record._id);
                                    setEditingStageDate(stageDatePST);
                                }}
                                style={{ color: '#1890ff' }}
                            />
                        </Tooltip>
                    )}
                    </div>
                )}
               
                {isEditing && (
                    <div style={{ marginTop: 8 }}>
                        <Space size={8} direction="vertical" style={{ width: '100%' }}>
                            <DatePicker
                                value={editingStageDate}
                                onChange={setEditingStageDate}
                                format="YYYY-MM-DD"
                                size="small"
                                style={{ width: '100%' }}
                                placeholder="Select date"
                            />
                            <Space size={4}>
                                <Button
                                    type="primary"
                                    size="small"
                                    icon={<CheckOutlined />}
                                    onClick={handleDateUpdate}
                                    loading={savingStageDate}
                                >
                                    Save
                                </Button>
                                <Button
                                    size="small"
                                    icon={<CloseOutlined />}
                                    onClick={() => {
                                        setEditingStageId(null);
                                        setEditingStageDate(null);
                                    }}
                                    disabled={savingStageDate}
                                >
                                    Cancel
                                </Button>
                            </Space>
                        </Space>
                    </div>
                )}
                
            </div>
        );
    },
},
{
    title: "Date",
    dataIndex: 'orderDate',
    key: 'orderDate',
    width: 210,
    render: (date, record) => (
        <Tooltip
            title={
                <>
                    <div><strong>Created:</strong> {record.createdAt ? formatToPST(record.createdAt) : 'N/A'}</div>
                    <div><strong>Updated:</strong> {record.updatedAt ? formatToPST(record.updatedAt) : 'N/A'}</div>
                </>
            }
        >
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>

              

                {/* Divider */}
                <div style={{ width: '100%', height: 0.5, background: '#e0e0e0' }} />

                {/* Created — Style 04 */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
                    <div style={{
                        width: 28, height: 28, borderRadius: 8,
                        background: '#F1EFE8',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0,
                    }}>
                        <ClockCircleOutlined style={{ fontSize: 13, color: '#888780' }} />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Text style={{ fontSize: 12, fontWeight: 500, lineHeight: 1.2 }}>
                            {record.createdAt ? dayjs(record.createdAt).format('MM/DD/YY · hh:mm A') : 'N/A'}
                        </Text>
                        <Text type="secondary" style={{ fontSize: 10, lineHeight: 1.2 }}>
                            Created · {record.createdAt ? dayjs(record.createdAt).fromNow() : '—'}
                        </Text>
                    </div>
                    
                </div>

                {/* Updated — Style 04 */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
                    <div style={{
                        width: 28, height: 28, borderRadius: 8,
                        background: '#E6F1FB',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0,
                    }}>
                        <SyncOutlined style={{ fontSize: 13, color: '#185FA5' }} />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Text style={{ fontSize: 12, fontWeight: 500, lineHeight: 1.2, color: '#185FA5' }}>
                            {record.updatedAt ? dayjs(record.updatedAt).format('MM/DD/YY · hh:mm A') : 'N/A'}
                        </Text>
                        <Text type="secondary" style={{ fontSize: 10, lineHeight: 1.2 }}>
                            Updated · {record.updatedAt ? dayjs(record.updatedAt).fromNow() : '—'}
                        </Text>
                    </div>
                </div>
  
            </div>
        </Tooltip>
    ),
},
        {
            title: '',
            key: 'actions',
            width: 80,
            fixed: 'right',
            render: (_, record) => (
                <Space>
                    <Tooltip title={record.personalInfo?.phoneNumber ? "Call Customer" : "No phone number"}>
                        <Button
                            icon={<PhoneOutlined />}
                            href={`tel:${record.personalInfo?.phoneNumber}`}
                            disabled={!record.personalInfo?.phoneNumber}
                            size="small"
                            shape="circle"
                            onClick={() => {
                                setCalledLeads(prev => new Set(prev).add(record._id));
                            }}
                        />
                    </Tooltip>
                    <Tooltip title="View Details">
                        <Button
                            icon={<EyeOutlined />}
                            onClick={() => navigate(`/lead-details/${record._id}`)}
                            size="small"
                            shape="circle"
                            type="primary"
                            style={{ 
                                background: `linear-gradient(135deg, #1890ff 0%, #40a9ff 100%)`,
                                border: 'none'
                            }}
                            disabled={
                                (record.leadStage === "New" && !calledLeads.has(record._id) && !isAdmin)
                            }
                        />
                    </Tooltip>
                </Space>
            ),
        },
    ];

    const isSearching = searchTerm?.trim().length > 0;

    return (
        <div style={{ 
            padding: 24, 
            minHeight: "100vh", 
            background: `linear-gradient(135deg, #f5f7fa 0%, #ffffff 100%)`
        }}>
            {/* Modern Header with Stats */}
            <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
            >
                <Card 
                    style={{ 
                        marginBottom: 24, 
                        borderRadius: 24,
                        border: 'none'
                    }}
                    bodyStyle={{ padding: '32px' }}
                >
                    <Row gutter={[24, 24]} align="middle">
                        <Col xs={24} md={12}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
                                <Avatar 
                                    size={72} 
                                    style={{ 
                                        background: 'rgba(255,255,255,0.2)',
                                        backdropFilter: 'blur(10px)',
                                        boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
                                        color:"black"
                                    }}
                                    icon={<CheckCircleOutlined style={{ fontSize: 36 }} />}
                                />
                                <div>
                                    <Title level={2} style={{ margin: 0,  fontWeight: 700 }}>
                                        Sales Made Complete
                                    </Title>
                                    <Text style={{ fontSize: 14 }}>
                                        Track and manage completed sales with detailed analytics
                                    </Text>
                                </div>
                            </div>
                        </Col>
                     
                    </Row>
                </Card>
            </motion.div>

            {/* Filter Section */}
            <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
            >
                <Card 
                    bordered={false}
                    style={{ 
                        borderRadius: 20,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                        marginBottom: 24,
                        border: '1px solid #f0f0f0'
                    }}
                    bodyStyle={{ padding: '20px 24px' }}
                >
                    <Row gutter={[16, 16]} align="middle">
                        <Col xs={24} md={8}>
                            <Space.Compact block>
                                <SearchFieldsSelect value={searchFields} onChange={setSearchFields} />
                                <Input
                                    placeholder={getSearchFieldsPlaceholder(searchFields)}
                                    prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    allowClear
                                    size="large"
                                />
                            </Space.Compact>
                        </Col>
                        {isAdmin && (
                            <Col xs={24} md={4}>
                                <Select
                                    placeholder="Filter by Agent"
                                    value={selectedAgentId}
                                    onChange={setSelectedAgentId}
                                    style={{ width: '100%', borderRadius: 12 }}
                                    size="large"
                                    allowClear
                                    suffixIcon={<TeamOutlined />}
                                    showSearch
                                    optionFilterProp="children"
                                >
                                    {agents.map(agent => (
                                        <Option key={agent._id} value={agent._id}>
                                            <Space>
                                                <Avatar size="small" icon={<UserOutlined />} />
                                                <span>{agent.firstName} {agent.lastName}</span>
                                            </Space>
                                        </Option>
                                    ))}
                                </Select>
                            </Col>
                        )}
                        <Col xs={24} md={4}>
                            <Select 
                                value={quickFilter} 
                                onChange={handleQuickFilter}
                                style={{ width: '100%', borderRadius: 12 }}
                                size="large"
                            >
                                <Option value="today">Today</Option>
                                <Option value="yesterday">Yesterday</Option>
                                <Option value="thisWeek">This Week</Option>
                                <Option value="lastWeek">Last Week</Option>
                                <Option value="currentMonth">Current Month</Option>
                                <Option value="lastMonth">Last Month</Option>
                                <Option value="thisYear">This Year</Option>
                            </Select>
                        </Col>
                        <Col xs={24} md={isAdmin ? 7 : 8}>
                            <RangePicker
                                value={dateRange}
                                 onChange={handleDateRangeChange}
                                disabledDate={(current) => current && current > dayjs().endOf('day')}
                                style={{ width: '100%', borderRadius: 12 }}
                                size="large"
                                placeholder={['Start Date', 'End Date']}
                            />
                        </Col>
                        <Col xs={24} md={isAdmin ? 4 : 6}>
                            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                                <Tooltip title="Refresh">
                                    <Button 
                                        icon={<ReloadOutlined />} 
                                        onClick={fetchLeads}
                                        loading={loading}
                                        size="large"
                                        style={{ borderRadius: 12 }}
                                    />
                                </Tooltip>
                                <Tooltip title="Clear Filters">
                                    <Button 
                                        icon={<ClearOutlined />} 
                                        onClick={clearFilters}
                                        size="large"
                                        style={{ borderRadius: 12 }}
                                    />
                                </Tooltip>
                            </Space>
                        </Col>
                    </Row>

                    {/* Date Range Info */}
<div style={{ 
    marginTop: 16,
    backgroundColor: '#f0f5ff', 
    padding: '8px 12px', 
    borderRadius: 12,
    display: 'flex',
    alignItems: 'center',
    gap: 8
}}>
    <CalendarOutlined style={{ color: '#1890ff' }} />
    <Text type="secondary">
        Showing sales from: <Text strong>
            {dateRange?.[0]?.isValid() ? dateRange[0].format('MM-DD-YYYY') : dayjs().startOf('month').format('MM-DD-YYYY')}
        </Text> to <Text strong>
            {dateRange?.[1]?.isValid() ? dateRange[1].format('MM-DD-YYYY') : dayjs().endOf('month').format('MM-DD-YYYY')}
        </Text>
    </Text>
    <Badge 
        count={totalRecords} 
        showZero 
        color="#52c41a" 
        style={{ marginLeft: 'auto' }}
    />
</div>
                </Card>
            </motion.div>

            {/* Results Header */}
            <div style={{
                padding: '16px 20px',
                background: '#ffffff',
                borderTop: '1px solid #f0f0f0',
                borderBottom: '1px solid #f0f0f0',
                borderRadius: '12px 12px 0 0',
                marginTop: 8
            }}>
                <Row justify="space-between" align="middle">
                    <Col>
                        <Space size={16}>
                        <Text strong style={{ fontSize: 16 }}>
    {isSearching ? (
        <>
            <SearchOutlined style={{ color: '#1890ff', marginRight: 8 }} />
            Search Results
        </>
    ) : (
        <>
            <CalendarOutlined style={{ marginRight: 8, color: '#1890ff' }} />
            {dateRange && dateRange[0] && dateRange[1] && dateRange[0].isValid() && dateRange[1].isValid() ? (
                dateRange[0].isSame(dateRange[1], 'day') ? (
                    dateRange[0].format('MM-DD-YYYY')
                ) : (
                    `${dateRange[0].format('MMM D')} → ${dateRange[1].format('MM-DD-YYYY')}`
                )
            ) : (
                `${dayjs().startOf('month').format('MMM D')} → ${dayjs().endOf('month').format('MM-DD-YYYY')}`
            )}
        </>
    )}
</Text>
                            <Badge
                                count={leads.length}
                                showZero
                                style={{
                                    backgroundColor: '#52c41a',
                                    fontSize: 12,
                                    fontWeight: 'bold'
                                }}
                            />
                            <Text type="secondary">
                                of {totalRecords.toLocaleString()} total records
                            </Text>
                        </Space>
                    </Col>
                    <Col>
                       <Pagination
    current={currentPage}
    pageSize={pageSize}
    total={totalRecords}
    showSizeChanger
    pageSizeOptions={[10, 20, 30, 50, 100]}
    onChange={handlePageChange}
    onShowSizeChange={handlePageSizeChange}
    
    showTotal={(total, range) =>
        `Showing ${range[0]}-${range[1]} of ${total} completed sales`
    }
    size="default"
/>
                    </Col>
                </Row>
            </div>

            {/* Main Table */}
            <Card 
                bordered={false}
                style={{ 
                    borderRadius: '0 0 20px 20px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                    overflow: 'hidden',
                    borderTop: 'none'
                }}
                bodyStyle={{ padding: 0 }}
            >
                <Table
                    columns={columns}
                    dataSource={leads}
                    rowKey={(record) => record._id || record.key}
                    loading={loading}
                    pagination={false}
                    onChange={handleTableChange}
                    scroll={{
                        x: isAdmin ? 1800 : 1600,
                        y: 'calc(100vh - 550px)'
                    }}
                    size="middle"
                    rowClassName={() => 'table-row-hover'}
                    className="sales-table"
                />

                {/* Bottom Pagination */}
                {leads.length > 0 && (
                    <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        marginTop: 32,
                        padding: '20px 24px',
                        background: '#fafafa',
                        borderRadius: 12,
                        margin: 24
                    }}>
                     <Pagination
    current={currentPage}
    pageSize={pageSize}
    total={totalRecords}
    showSizeChanger
    pageSizeOptions={[10, 20, 30, 50, 100]}
    onChange={handlePageChange}
    onShowSizeChange={handlePageSizeChange}
    
    showTotal={(total, range) =>
        `Showing ${range[0]}-${range[1]} of ${total} completed sales`
    }
    size="default"
/>
                    </div>
                )}
            </Card>

            <style>{`
                .table-row-hover:hover {
                    background: linear-gradient(90deg, #f0f7ff 0%, #ffffff 100%) !important;
                    cursor: pointer;
                    transition: all 0.3s ease;
                }
                
                .ant-table-tbody > tr > td {
                    border-bottom: 1px solid #f0f0f0 !important;
                }
                
                .ant-table-thead > tr > th {
                    background: #fafafa !important;
                    font-weight: 600 !important;
                    font-size: 13px !important;
                    color: #1f2937 !important;
                    border-bottom: 2px solid #f0f0f0 !important;
                }
            `}</style>

             <Modal
                     
                      open={commentsModalVisible}
                      onCancel={() => {
                          setCommentsModalVisible(false);
                          setSelectedLeadForComment(null);
                          setNewComment('');
                      }}
                      footer={[
                          <Button key="cancel" onClick={() => {
                              setCommentsModalVisible(false);
                              setSelectedLeadForComment(null);
                              setNewComment('');
                          }}>
                              Cancel
                          </Button>,
                          <Button 
                              key="submit" 
                              type="primary" 
                              onClick={handleAddComment}
                              loading={addingComment}
                              disabled={!newComment.trim()}
                          >
                              Add Comment
                          </Button>
                      ]}
                      width={500}
                  >
                      <div style={{ padding: '16px 0' }}>
                        
                  
                          <div>
                              <Text strong>Comment:</Text>
                              <Input.TextArea
                                  placeholder="Enter your comment here..."
                                  value={newComment}
                                  onChange={(e) => setNewComment(e.target.value)}
                                  rows={4}
                                  maxLength={1000}
                                  showCount
                                  style={{ marginTop: '8px' }}
                                  autoFocus
                              />
                          </div>
                  
                          {/* Show existing comments if any */}
                          {selectedLeadForComment?.saleMadePageComment?.length > 0 && (
                              <div style={{ marginTop: '20px' }}>
                                  <Divider />
                                  <Text strong>Previous Comments ({selectedLeadForComment.saleMadePageComment.length})</Text>
                                  <div style={{ 
                                      maxHeight: '200px', 
                                      overflowY: 'auto', 
                                      marginTop: '8px',
                                      padding: '8px',
                                      background: '#f5f5f5',
                                      borderRadius: '8px'
                                  }}>
                                      {[...selectedLeadForComment.saleMadePageComment]
                                          .sort((a, b) => new Date(b.changedAt) - new Date(a.changedAt))
                                          .map((comment, idx) => (
                                              <div key={idx} style={{ 
                                                  marginBottom: '12px', 
                                                  padding: '8px',
                                                  background: 'white',
                                                  borderRadius: '6px',
                                                  border: '1px solid #e8e8e8'
                                              }}>
                                                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                                                      <Text type="secondary" style={{ fontSize: '11px' }}>
                                                          <ClockCircleOutlined style={{ marginRight: '4px' }} />
                                                          {formatCommentDate(comment.changedAt)}
                                                      </Text>
                                                  </div>
                                                  <Text style={{ fontSize: '13px' }}>{comment.comment}</Text>
                                              </div>
                                          ))}
                                  </div>
                              </div>
                          )}
                      </div>
                  </Modal>
                  {/* Service Agent Assignment Modal */}
<Modal
    title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
                width: 40,
                height: 40,
                borderRadius: 12,
                background: 'linear-gradient(135deg, #52c41a 0%, #73d13d 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}>
                <TeamOutlined style={{ fontSize: 20, color: 'white' }} />
            </div>
            <div>
                <Title level={4} style={{ margin: 0 }}>Assign Service Agent</Title>
                <Text type="secondary" style={{ fontSize: 12 }}>
                    Select service agent for this lead
                </Text>
            </div>
        </div>
    }
    open={serviceAgentModalVisible}
    onCancel={() => {
        setServiceAgentModalVisible(false);
        setSelectedLeadForServiceAgent(null);
        setSelectedServiceAgentForLead(null);
    }}
    footer={null}
    width={500}
    centered
>
    <div style={{ padding: '16px 0' }}>
        {selectedLeadForServiceAgent && (
            <>
                {/* Customer Info Card */}
                <Card 
                    size="small" 
                    style={{ 
                        marginBottom: 20,
                        background: '#f5f5f5',
                        borderRadius: 12,
                        border: 'none'
                    }}
                    bodyStyle={{ padding: '12px 16px' }}
                >
                    <Space direction="vertical" size={4} style={{ width: '100%' }}>
                        <Text strong>
                            {selectedLeadForServiceAgent.personalInfo?.fullName || 'N/A'}
                        </Text>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            {selectedLeadForServiceAgent.vehicleInfo?.make} {selectedLeadForServiceAgent.vehicleInfo?.model} - {selectedLeadForServiceAgent.vehicleInfo?.part}
                        </Text>
                        {selectedLeadForServiceAgent.serviceHandledBy && (
                            <div style={{ 
                                marginTop: 8,
                                paddingTop: 8,
                                borderTop: '1px solid #e8e8e8'
                            }}>
                                <Text type="secondary" style={{ fontSize: 11 }}>
                                    Currently assigned: 
                                </Text>
                                <Text strong style={{ fontSize: 12, color: '#52c41a', marginLeft: 8 }}>
                                    {getServiceAgentName(selectedLeadForServiceAgent)}
                                </Text>
                            </div>
                        )}
                    </Space>
                </Card>

                {/* Service Agent Selection */}
                <div style={{ marginBottom: 24 }}>
                    <Text strong style={{ display: 'block', marginBottom: 8 }}>
                        Select Service Agent:
                    </Text>
                    <Select
                        value={selectedServiceAgentForLead}
                        onChange={setSelectedServiceAgentForLead}
                        style={{ width: '100%' }}
                        size="large"
                        placeholder="Choose an agent"
                        allowClear
                        showSearch
                        optionFilterProp="children"
                        loading={loadingServiceAgents}
                    >
                        {Object.entries(serviceAgentsMap).map(([agentId, agentInfo]) => (
                            <Option key={agentId} value={agentId}>
                                <Space direction="vertical" size={0}>
                                    <Text strong>{agentInfo.name}</Text>
                                   
                                </Space>
                            </Option>
                        ))}
                    </Select>
                    <Text type="secondary" style={{ fontSize: 11, marginTop: 4, display: 'block' }}>
                        {selectedServiceAgentForLead ? 'Agent will be assigned to this lead' : 'Clear to unassign the current agent'}
                    </Text>
                </div>

                {/* Modal Actions */}
                <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                    <Button 
                        onClick={() => {
                            setServiceAgentModalVisible(false);
                            setSelectedLeadForServiceAgent(null);
                            setSelectedServiceAgentForLead(null);
                        }} 
                        size="large"
                    >
                        Cancel
                    </Button>
                    <Button 
                        type="primary" 
                        onClick={handleServiceAgentSave}
                        loading={savingServiceAgent}
                        size="large"
                        icon={<CheckOutlined />}
                    >
                        {selectedServiceAgentForLead ? 'Assign Agent' : 'Unassign Agent'}
                    </Button>
                </div>
            </>
        )}
    </div>
</Modal>
        </div>
    );
};

export default SaleMadeComplete;
